import { createSlice } from "@reduxjs/toolkit";
import { INITIAL_PAGE } from "constants/app";
import { documentBy } from "constants/discovery";
import * as themeReducers from "slices/discovery/helpers/theme";
import * as filtersReducers from "slices/discovery/helpers/filters";
import * as configReducers from "slices/discovery/helpers/config";

export const initialState = {
  themes: [],
  uniqueItemCount: 0,
  currentUniqueItemCount: 0,
  config: {
    folderId: undefined,
    documentId: undefined,
    showAll: true,
    isSearch: false,
    isSnapshot: false,
    saveSnapshotDisabled: false,
    loading: [],
    documentBy: documentBy.theme,
    byContentPage: INITIAL_PAGE,
    byThemeContentPage: INITIAL_PAGE,
    searchTerm: "",
    byContentHoveredTerm: null,
    filterView: false,
    filterType: null,
    filterDocID: null,
  },
  content: { data: [], previousDocItemsArray: [] },
  snapshots: [],
  byContent: [],
  addToDictPanel: {
    show: false,
    // All selected terms
    terms: [],
    // Term on which 'add to dict' action was triggered
    term: null,
    // List of dictionaries
    dictionaries: [],
    // Selected dictionary
    dictionary: null,
    step: 1,
    dictionaryError: null,
    selectedDictionaryId: null,
    newlyCreatedDictionaryId: null,
    selectedCategoryName: null,
    newlyCreatedCategoryName: null,
  },
};

export const discoverySlice = createSlice({
  name: "discovery",
  initialState,
  reducers: {
    // Themes
    setThemes: (state, action) => {
      state.themes = action.payload;
    },
    setUniqueItemCount: (state, action) => {
      state.uniqueItemCount = action.payload;
    },
    setCurrentUniqueItemCount: (state, action) => {
      state.currentUniqueItemCount = action.payload;
    },
    setThemesItems: themeReducers.setThemesItems,
    expandTheme: themeReducers.expandTheme,
    collapseTheme: (state, action) =>
      themeReducers.expandCollapseTheme(state, action.payload, true),
    collapseAllThemes: themeReducers.collapseAllThemes,
    check: themeReducers.check,
    uncheckAllThemes: themeReducers.uncheckAllThemes,
    uncheckAndCollapse: themeReducers.uncheckAndCollapse,
    addIgnoredDocument: themeReducers.addIgnoredDocument,
    removeIgnoredDocument: themeReducers.removeIgnoredDocument,
    resetThemes: themeReducers.resetThemes,
    filterThemes: themeReducers.filterThemes,
    removeNounPhrases: themeReducers.removeNounPhrases,
    addSearchNounPhrasesToState: themeReducers.addSearchNounPhrasesToState,
    clearSearchState: themeReducers.clearSearchState,

    // Filters
    setFilterView: (state, action) => {
      state.config.filterView = action.payload;
    },
    setFilterType: (state, action) => {
      state.config.filterType = action.payload;
    },
    setFilterDocID: (state, action) => {
      state.config.filterDocID = action.payload;
    },
    clearFilterState: filtersReducers.clearFilterState,
    filterData: filtersReducers.filterData,
    setDisabledDocument: (state, action) => {
      state.themes[0].Documents = state.themes[0].Documents.map((document) => {
        return {
          ...document,
          disabled: document.Id !== action.payload,
        };
      });
    },

    // Config
    setShowAll: (state, action) => {
      state.config.showAll = action.payload;
    },
    openSnapshot: (state) => {
      state.config.isSnapshot = true;
      state.config.isSearch = false;
    },
    closeSnapshot: (state) => {
      state.config.isSnapshot = false;
      state.config.isSearch = false;
    },
    setSearchTerm: (state, action) => {
      state.config.searchTerm = action.payload;
    },
    setSearch: (state, action) => {
      state.config.isSearch = action.payload;
    },
    setByContentHoveredTerm: (state, action) => {
      state.config.byContentHoveredTerm = action.payload;
    },
    setByContentCurrentPage: (state, action) => {
      state.config.byContentPage = action.payload;
    },
    setByThemeContentPage: (state, action) => {
      state.config.byThemeContentPage = action.payload;
    },
    setDisableSaveSnapshot: (state, action) => {
      state.config.saveSnapshotDisabled = action.payload;
    },
    setSelectedFolderAndDocument: (state, action) => {
      state.config.folderId = action.payload.folderId;
      state.config.documentId = action.payload.documentId;
    },
    setDocumentBy: (state, action) => {
      state.config.documentBy = action.payload;
    },
    setLoading: configReducers.setLoading,
    expandCollapse: configReducers.expandCollapse,
    showAllThemesAndItems: configReducers.showAllThemesAndItems,

    // Snapshot
    setSnapshots: (state, action) => {
      state.snapshots = action.payload;
    },

    // Content
    canLoadContent: (state, action) => {
      state.content.loadContent = action.payload;
    },
    setByContentPage: (state, action) => {
      state.config.byContentPage = action.payload;
    },
    setDataPrevLength: (state) => {
      const prevLength = state.content.data ? state.content.data.length : 0;
      state.content.previousLength = prevLength;
    },
    setContentData: (state, action) => {
      state.content.data = action.payload;
    },
    setPreviousDocItems: (state, action) => {
      state.content.previousDocItemsArray = action.payload;
    },

    // By content
    setByContentData: (state, action) => {
      state.byContent = action.payload;
    },

    // Add to dictionary panel
    showAddToDictPanel: (state, action) => {
      state.addToDictPanel.show = true;
      state.addToDictPanel.terms = action.payload.terms;
      state.addToDictPanel.term = action.payload.term;
    },
    hideAddToDictPanel: (state) => {
      state.addToDictPanel.show = false;
      state.addToDictPanel.terms = [];
      state.addToDictPanel.term = null;
      state.addToDictPanel.dictionaries = [];
      state.addToDictPanel.dictionary = null;
      state.addToDictPanel.step = 1;
      state.addToDictPanel.dictionaryError = null;
      state.addToDictPanel.selectedDictionaryId = null;
      state.addToDictPanel.newlyCreatedDictionaryId = null;
      state.addToDictPanel.selectedCategoryName = null;
      state.addToDictPanel.newlyCreatedCategoryName = null;
    },
    setDictionaries: (state, action) => {
      state.addToDictPanel.dictionaries = action.payload;
    },
    setDictionary: (state, action) => {
      state.addToDictPanel.dictionary = action.payload;
    },
    setStep: (state, action) => {
      state.addToDictPanel.step = action.payload;
    },
    setDictionaryError: (state, action) => {
      state.addToDictPanel.dictionaryError = action.payload;
    },
    setSelectedDictionaryId: (state, action) => {
      state.addToDictPanel.selectedDictionaryId = action.payload;
    },
    setNewlyCreatedDictionaryId: (state, action) => {
      state.addToDictPanel.newlyCreatedDictionaryId = action.payload;
    },
    setSelectedCategoryName: (state, action) => {
      state.addToDictPanel.selectedCategoryName = action.payload;
    },
    setNewlyCreatedCategoryName: (state, action) => {
      state.addToDictPanel.newlyCreatedCategoryName = action.payload;
    },
  },
});

export const {
  setShowAll,
  setSnapshots,
  setCurrentUniqueItemCount,
  setUniqueItemCount,
  setFilterDocID,
  clearSearchState,
  removeNounPhrases,
  addSearchNounPhrasesToState,
  clearFilterState,
  setFilterType,
  setFilterView,
  setDisabledDocument,
  filterData,
  setThemes,
  setThemesItems,
  expandTheme,
  collapseTheme,
  collapseAllThemes,
  check,
  uncheckAllThemes,
  uncheckAndCollapse,
  addIgnoredDocument,
  removeIgnoredDocument,
  resetThemes,
  filterThemes,
  openSnapshot,
  closeSnapshot,
  setSearchTerm,
  setSearch,
  setByContentHoveredTerm,
  setByContentCurrentPage,
  setByThemeContentPage,
  setDisableSaveSnapshot,
  setSelectedFolderAndDocument,
  setDocumentBy,
  setLoading,
  expandCollapse,
  showAllThemesAndItems,
  canLoadContent,
  setByContentPage,
  setDataPrevLength,
  setContentData,
  setPreviousDocItems,
  setByContentData,
  showAddToDictPanel,
  hideAddToDictPanel,
  setDictionaries,
  setDictionary,
  setStep,
  setDictionaryError,
  setSelectedDictionaryId,
  setNewlyCreatedDictionaryId,
  setSelectedCategoryName,
  setNewlyCreatedCategoryName,
} = discoverySlice.actions;

export default discoverySlice.reducer;
